import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";
import { Col, Grid, Row, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "motion/react";
import {
  faInstagram,
  faSpotify,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import { getUserInvites } from "shared/service/StrmService";
import { moneyFormatObject, signByCurrency } from "shared/helpers/BRLFormat";
import { isMobile } from "react-device-detect";
import {
  dataLayerClickedEvent,
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";
import { HomeButtonClickEventName } from "event-tracker/button-click-event-name";
import { notifyFirstAccess, UserState } from "explorer/reducers/users";
import {
  ArtistState,
  getUserArtistMilestones,
} from "explorer/reducers/artistas";
import { MilestoneRatioPeriod } from "shared/enums/MilestoneRatioPeriod";
import { ReactComponent as AdvanceCTAIcon } from "shared/assets/images/icon-advance-cta-home.svg";
import { ReactComponent as AchievementsIcon } from "shared/assets/images/icon-achievements.svg";
import { ReactComponent as LevelMenu } from "shared/assets/images/icon-level.svg";
import { ReactComponent as PromotionIcon } from "shared/assets/images/icon-promotion.svg";
import { ReactComponent as IconMetas } from "shared/assets/images/icon-goals-side-menu.svg";
import Meta from "../metas/Meta";
import QuickQuincy from "shared/components/quick-quincy";
import "./Home.scss";
import { DistributionStatusEnum } from "shared/enums/DistributionStatusEnum";
import { useFlags } from "launchdarkly-react-client-sdk";
import HomeSkeleton from "./HomeSkeleton";
import {
  AdvanceState,
  canAskAdvance,
  getLoggedUserCashAdvanceAvailable,
} from "explorer/reducers/adiantamentos";
import { CashAdvanceAvailableEnum } from "shared/enums/CashAdvanceAvailable";
import SubscriptionDefaultCTA from "shared/components/subscription/SubscriptionDefaultCTA";
import ModalSignatureComplete from "./ModalSignature";
import ModalBreakingSound from "./modal-breaking-sound/ModalBreakingSound";
import ModalSubscriptionRelease from "../new-estagio-artista/ModalSubscriptionRelease";
import CardCTAAnnoDomini from "../new-estagio-artista/campanhas-especiais/ctas/CardCTAAnnoDomini";

const { useBreakpoint } = Grid;

enum Tab {
  Spotify = "spotify",
  Youtube = "youtube",
  Instagram = "instagram",
  Achievements = "achievements",
}

const BadgeStage: React.FC<{ stage: number }> = ({ stage }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="19"
      height="20"
      viewBox="0 0 19 20"
      fill="none"
    >
      <path
        d="M7.11276 0.679505C8.47728 -0.226894 10.2522 -0.226894 11.6167 0.679505L16.5409 3.95043C17.6013 4.65485 18.2711 5.81488 18.3509 7.08546L18.7215 12.9854C18.8242 14.6203 17.9368 16.1574 16.4695 16.8859L11.1748 19.5149C10.0345 20.0811 8.695 20.0811 7.55474 19.5149L2.25996 16.8859C0.792733 16.1574 -0.0947347 14.6203 0.00797093 12.9854L0.378606 7.08546C0.458424 5.81488 1.12817 4.65485 2.18861 3.95043L7.11276 0.679505Z"
        fill="#F2F2F2"
      />
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        fill="#1A1A1A"
        fontSize="12"
      >
        {stage}
      </text>
    </svg>
  );
};

const Home: React.FC<RouteComponentProps> = (props) => {
  const { history, location } = props;
  const screens = useBreakpoint();
  const [loadingInvite, setLoadingInvite] = useState<boolean>(false);
  const [invites, setInvites] = useState<any[]>([]);
  const [checkedInvites, setCheckedInvites] = useState(false);
  const [selectedTab, setSelectedTab] = useState<Tab>(Tab.Spotify);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    loggedUser,
    loading,
    firstAccess,
    firstAccessNotified,
    shouldOpenTour,
  } = useSelector((state: UserState) => state.users);
  const {
    userArtist,
    loading: loadingArtist,
    milestones,
  } = useSelector((state: ArtistState) => state.artists);

  const { cashAdvanceAvailable } = useSelector(
    (state: AdvanceState) => state.advances,
  );
  const { showHome, enableTour } = useFlags();
  const level = userArtist.stage || 0;
  let { titulo } = t(`pages.EstagioArtista.estagio.${level}`) as any;
  let { titulo: tituloProximo } = t(
    `pages.EstagioArtista.estagio.${level + 1}`,
  ) as any;

  const noData =
    !milestones?.achievements &&
    !milestones?.instagram &&
    !milestones?.spotify &&
    !milestones?.youtube;

  const permiteAssinarDistroStrm = () =>
    false && // ocultado a pedido do time de produto em 16/09/2025
    userArtist?.distribution?.available &&
    userArtist?.distribution?.status === DistributionStatusEnum.NotSigned;

  const getPropBySelectedTab = () => {
    if (!milestones) return [];
    return milestones[selectedTab];
  };

  const mensagemSemAdiantamento = () => {
    if (
      cashAdvanceAvailable === CashAdvanceAvailableEnum.NotAvailable ||
      cashAdvanceAvailable === CashAdvanceAvailableEnum.NotFound
    )
      return t("pages.Adiantamentos.naoCumpreRequisitosMensagemSimples", {
        currency: signByCurrency(userArtist?.cashAdvanceEstimated?.currency),
      });

    if (cashAdvanceAvailable === CashAdvanceAvailableEnum.NeedRetry)
      return t("pages.Adiantamentos.tentarNovamenteEmbreve");

    // if (cashAdvanceAvailable === CashAdvanceAvailableEnum.NotFound)
    //   return t("pages.Adiantamentos.spotifyNaoEncontrado");
  };

  const onClickTab = (tab: Tab) => {
    setSelectedTab(tab);
    dataLayerClickedEvent(HomeButtonClickEventName.GOALS_INTERACTION, {
      ...tagManagerEvent.Home.S4A_HOME_DIAGNOSED,
      businessContext: "HOME_V2",
    });
  };

  const onClickViewMoreAdvance = () => {
    history.push("/adiantamentos");
    dataLayerClickedEvent(HomeButtonClickEventName.ADVANCE_VIEW_MORE, {
      ...tagManagerEvent.Home.S4A_HOME_DIAGNOSED,
      businessContext: "HOME_V2",
    });
  };

  const onClickBoostMeta = (meta: any) => {
    if (meta.name === "Followers_cnt_spotify") {
      dataLayerClickedEvent(HomeButtonClickEventName.BOOST_SPOTIFY_FOLLOWERS, {
        ...tagManagerEvent.Home.S4A_HOME_DIAGNOSED,
        businessContext: "HOME_V2",
      });
    }
    if (meta.name === "Popularity_spotify") {
      dataLayerClickedEvent(HomeButtonClickEventName.BOOST_SPOTIFY_POPULARITY, {
        ...tagManagerEvent.Home.S4A_HOME_DIAGNOSED,
        businessContext: "HOME_V2",
      });
    }
    history.push("/promotion");
  };

  useEffect(() => {
    const fetchInvites = async () => {
      setLoadingInvite(true);
      const response: any = await getUserInvites();
      setInvites(response || []);
      setCheckedInvites(true);
      setLoadingInvite(false);
    };
    if (!loading && !loadingInvite && !checkedInvites) fetchInvites();
  }, [loading, loadingInvite, checkedInvites]);

  useEffect(() => {
    if (!checkedInvites) return;
    if (invites.length > 0) {
      history.push("/convites");
    } else if (!showHome && (userArtist?.stage ?? 0) > 0) {
      history.push("/estagio");
    }
  }, [checkedInvites, invites, userArtist?.stage, history, showHome]);

  useEffect(() => {
    if (!loading && !userArtist?.stage) {
      dataLayerViewedEvent({
        ...tagManagerEvent.Home.S4A_HOME_UNDIAGNOSED,
      });
    }
  }, [loading, userArtist]);

  useEffect(() => {
    if (userArtist.id) {
      dispatch(
        getUserArtistMilestones({
          artistId: userArtist.id,
          period: MilestoneRatioPeriod.TwelveMonths,
        }),
      );
    }
  }, [userArtist, dispatch]);

  useEffect(() => {
    if (
      !loading &&
      !loadingArtist &&
      (userArtist?.stage || 0) > 0 &&
      !firstAccessNotified
    ) {
      dataLayerViewedEvent({
        ...tagManagerEvent.Home.S4A_HOME_DIAGNOSED,
        businessContext: "HOME_V2",
        carreerStage: userArtist?.stage,
        approvedLimit: userArtist?.cashAdvanceEstimated?.amountUsd,
        alreadyCalculated:
          userArtist?.cashAdvanceEstimated?.successfullyEstimated,
        country: userArtist?.country,
        firstAccess: firstAccess,
        activeAdvanceContract: userArtist?.cashAdvanceSigned,
        activeDistributionContract:
          userArtist?.distribution.status === DistributionStatusEnum.Signed,
        registerDate: loggedUser?.firstLoginArtistsAt,
      });

      dispatch(notifyFirstAccess());
    }
  }, [
    loading,
    firstAccessNotified,
    loggedUser,
    userArtist,
    dispatch,
    history,
    firstAccess,
    loadingArtist,
  ]);

  useEffect(() => {
    if (enableTour && shouldOpenTour) {
      history.push({
        pathname: "/tour",
        search: location.search || "",
      });
    }
  }, [enableTour, history, shouldOpenTour, location.search]);

  useEffect(() => {
    try {
      if (location?.pathname === "/") {
        history.replace("/home");
      }
    } catch (e) {}
  }, [history, location]);

  useEffect(() => {
    dispatch(getLoggedUserCashAdvanceAvailable(false));
  }, [dispatch]);

  // if (loading || loadingArtist) {
  //   if (!showHome && userArtist?.stage) {
  //     return <Loader.Simple />;
  //   }
  //   return <HomeSkeleton />;
  // }

  if (loading || loadingArtist || (!showHome && userArtist?.stage)) {
    return <HomeSkeleton />;
  }

  if (level > 0 && showHome) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
        className={`home-page ${isMobile ? "mobile" : ""}`}
      >
        <ModalSignatureComplete />
        <ModalBreakingSound />
        <ModalSubscriptionRelease />
        <div
          style={{ display: "flex", justifyContent: "center" }}
          className="home-container"
        >
          <Row
            justify="center"
            gutter={[isMobile ? 16 : 32, isMobile ? 16 : 32]}
            style={{
              maxWidth: isMobile ? "auto" : 1400,
              width: "100%",
            }}
          >
            {!screens.lg && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  position: "relative",
                  zIndex: 1,
                  marginBottom: 12,
                }}
              >
                <SubscriptionDefaultCTA />
              </div>
            )}
            <Col md={24} lg={8}>
              <div>
                <div
                  className="artist-feature-card"
                  onClick={() => history.push("/estagio")}
                  style={{ cursor: "pointer" }}
                >
                  <div
                    className="artist-feature-card-bg"
                    style={{
                      backgroundImage: `url(${userArtist?.profileImage || ""})`,
                    }}
                  >
                    <div className="level-badge">
                      <BadgeStage stage={userArtist?.stage || 0} />
                      <span className="level-label-text">{titulo}</span>
                    </div>

                    <div className="card-content">
                      <div className="welcome-label">
                        {t("pages.Home.welcomeTitle")}
                      </div>
                      <div className="artist-name heading-h4">
                        {userArtist?.name}
                      </div>
                      {level < 10 && (
                        <div className="next-level">
                          <span className="next-level-label">
                            {t("pages.Home.nextLevel")} :{" "}
                          </span>
                          <b style={{ opacity: 1 }}>{tituloProximo}</b>
                        </div>
                      )}

                      <div className="progress-row">
                        <div className="progress-track" aria-hidden>
                          <div
                            className="progress-filled"
                            style={{
                              width: `${userArtist?.stagePercentual || 0}%`,
                            }}
                          />
                        </div>

                        <div
                          className="percent-pill"
                          style={{
                            left: `calc(${
                              userArtist?.stagePercentual || 0
                            }% - 16px)`,
                          }}
                        >
                          {(userArtist?.stagePercentual || 0).toFixed(0)}%
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="quincy-step">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 8,
                    }}
                  >
                    <span className="title">{t("pages.Home.nextMoves")}</span>
                    <Link
                      to="/quincy"
                      className="green-label"
                      onClick={() => {
                        dataLayerClickedEvent(
                          HomeButtonClickEventName.QUINCY_VIEW_MORE,
                          {
                            ...tagManagerEvent.Home.S4A_HOME_DIAGNOSED,
                            businessContext: "HOME_V2",
                          },
                        );
                      }}
                    >
                      {t("pages.Home.viewMore")}
                    </Link>
                  </div>
                  <QuickQuincy.Home />
                </div>
              </div>
            </Col>
            <Col md={24} lg={16}>
              <div>
                {screens.lg && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      position: "relative",
                      zIndex: 1,
                      marginBottom: 12,
                    }}
                  >
                    <SubscriptionDefaultCTA />
                  </div>
                )}
                {/* <div
                  style={{
                    marginTop: isMobile ? 20 : 40,
                    height: "1px",
                    backgroundColor: "#1B1A4C",
                  }}
                /> */}
                <CardCTAAnnoDomini mobile={isMobile} />
                <div className="goals">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 8,
                    }}
                  >
                    <span
                      className="title"
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <IconMetas className="icon" stroke="#bcb8f7" />
                      {t("pages.Home.goals")}
                    </span>
                    <Link
                      to="/metas"
                      className="green-label"
                      onClick={() => {
                        dataLayerClickedEvent(
                          HomeButtonClickEventName.GOALS_VIEW_MORE,
                          {
                            ...tagManagerEvent.Home.S4A_HOME_DIAGNOSED,
                            businessContext: "HOME_V2",
                          },
                        );
                      }}
                    >
                      {t("pages.Home.viewMore")}
                    </Link>
                  </div>
                  <div>
                    <div className="scrollmenu" hidden={noData}>
                      <div className="scroll-box">
                        <Tooltip title="Spotify">
                          <button
                            className={`body-regular ${
                              selectedTab === "spotify" ? "selected" : ""
                            }`}
                            onClick={() => {
                              onClickTab(Tab.Spotify);
                            }}
                          >
                            <FontAwesomeIcon icon={faSpotify} color="#56567C" />
                            Spotify
                          </button>
                        </Tooltip>
                      </div>
                      <div className="scroll-box">
                        <Tooltip title="Youtube">
                          <button
                            className={`body-regular ${
                              selectedTab === "youtube" ? "selected" : ""
                            }`}
                            onClick={() => onClickTab(Tab.Youtube)}
                          >
                            <FontAwesomeIcon icon={faYoutube} color="#56567C" />
                            YouTube
                          </button>
                        </Tooltip>
                      </div>
                      <div className="scroll-box">
                        <Tooltip title="Instagram">
                          <button
                            className={`body-regular ${
                              selectedTab === "instagram" ? "selected" : ""
                            }`}
                            onClick={() => onClickTab(Tab.Instagram)}
                          >
                            <FontAwesomeIcon
                              icon={faInstagram}
                              color="#56567C"
                            />
                            Instagram
                          </button>
                        </Tooltip>
                      </div>
                      <div className="scroll-box">
                        <Tooltip title={t(`pages.Metas.conquistas`)}>
                          <button
                            className={`body-regular ${
                              selectedTab === "achievements" ? "selected" : ""
                            }`}
                            onClick={() => onClickTab(Tab.Achievements)}
                          >
                            <AchievementsIcon stroke="#56567C" />
                            {t(`pages.Metas.conquistas`)}
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div
                      className="meta-container"
                      style={{ minHeight: loading ? 200 : 0 }}
                    >
                      {getPropBySelectedTab()?.map((meta, idx) => (
                        <Meta
                          {...meta}
                          key={meta.name}
                          animationDelay={idx * 0.1}
                          footer={
                            selectedTab === Tab.Spotify && (
                              <div
                                className="meta-footer body-small"
                                onClick={() => onClickBoostMeta(meta)}
                              >
                                <PromotionIcon stroke="#F680C8" width={16} />
                                <span>
                                  <b
                                    className="bold-text"
                                    style={{ display: "block" }}
                                  >
                                    {t("pages.Home.promoteYourSongTitle")}
                                  </b>
                                  {t("pages.Home.promoteYourSongText")}
                                </span>
                              </div>
                            )
                          }
                        />
                      ))}
                    </div>
                  </div>
                </div>
                <div className="advance-section">
                  <div className="advance-content">
                    <div className="advance-card">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: 8,
                        }}
                      >
                        <span
                          className="title"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <AdvanceCTAIcon stroke="#CECDFF" />
                          {t("pages.Home.advance")}
                        </span>
                        <Link
                          to="/adiantamentos"
                          className="green-label"
                          onClick={() => {
                            dataLayerClickedEvent(
                              HomeButtonClickEventName.ADVANCE_VIEW_MORE,
                              {
                                ...tagManagerEvent.Home.S4A_HOME_DIAGNOSED,
                                businessContext: "HOME_V2",
                              },
                            );
                          }}
                        >
                          {t("pages.Home.viewMore")}
                        </Link>
                      </div>
                      <div className="advance-card-gradient-border">
                        <div className="advance-card">
                          <div className="advance-content">
                            {canAskAdvance(cashAdvanceAvailable) ||
                            loggedUser?.isCashAdvanceAvailable ? (
                              <>
                                <div
                                  className="advance-amount"
                                  style={{ marginTop: 24 }}
                                >
                                  <span
                                    className={`amount-text ${
                                      isMobile ? "body-reguler" : "heading-h5"
                                    }`}
                                  >
                                    {moneyFormatObject(
                                      userArtist?.cashAdvanceEstimated,
                                    )}{" "}
                                    {t("pages.Home.available")}
                                  </span>
                                </div>
                                <p
                                  className={`advance-description ${
                                    isMobile ? "body-small" : "body-regular"
                                  }`}
                                >
                                  {t("pages.Home.advanceCTA")}
                                </p>
                                <button
                                  className="botao-branco"
                                  style={{
                                    marginTop: 16,
                                    fontSize: 14,
                                  }}
                                  onClick={onClickViewMoreAdvance}
                                >
                                  {t("pages.Home.requestAdvance")}
                                </button>
                              </>
                            ) : (
                              <>
                                <p className="branco">
                                  {t(
                                    "pages.EstagioArtista.adiantamentoNaoLiberadoTitulo",
                                  )}
                                </p>
                                <p
                                  className={`advance-description ${
                                    isMobile ? "body-small" : "body-regular"
                                  }`}
                                >
                                  <Trans i18nKey={mensagemSemAdiantamento()} />
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {permiteAssinarDistroStrm() && (
                  <div className="cta-distro-card">
                    <div className="background" />
                    <div className="content">
                      <span
                        className="body-small"
                        style={{
                          color: "#cecdff",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <LevelMenu width={21} stroke="#bcb8f7" />
                        {t("pages.Home.distroCTATitle")?.toLocaleUpperCase()}
                      </span>
                      <h3 className="heading-h4">
                        {t("pages.Home.distroCTASubtitle")}
                      </h3>
                      <button
                        className="botao-gradiente-rosa-roxo"
                        style={{ width: "100%" }}
                        onClick={() => history.push("/distribua")}
                      >
                        {t("pages.Home.distroCTA")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="home">
      <div className="direction">
        <div className="content">
          <h1>{t("pages.Home.welcome")}</h1>
          <h3>{t("pages.Home.subtext")}</h3>
          <span className="message">{t("pages.Home.intro")}</span>
          <button
            className="botao-roxo"
            onClick={() => {
              dataLayerClickedEvent(HomeButtonClickEventName.RUN_DIAGNOSE, {
                ...tagManagerEvent.Home.S4A_HOME_UNDIAGNOSED,
              });

              history.push("/quiz");
            }}
          >
            {t("pages.Home.start")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Home;
